import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import OrderCard from "@/components/laundry/admin/OrderCard";
import OrderDetail from "@/components/laundry/admin/OrderDetail";
import { useApp } from "@/lib/AppContext";

const STATUS_TABS_AR = [
  { key: "all", label: "الكل", icon: "📋" },
  { key: "pending", label: "جديد", icon: "📥" },
  { key: "assigned", label: "مُعيَّن", icon: "👤" },
  { key: "picked_up", label: "تم الاستلام", icon: "🚗" },
  { key: "sorting", label: "فرز", icon: "🔍" },
  { key: "processing", label: "تنظيف", icon: "🫧" },
  { key: "ready", label: "جاهز", icon: "✅" },
  { key: "delivering", label: "توصيل", icon: "🚚" },
  { key: "delivered", label: "مُسلَّم", icon: "🎉" },
];
const STATUS_TABS_EN = [
  { key: "all", label: "All", icon: "📋" },
  { key: "pending", label: "New", icon: "📥" },
  { key: "assigned", label: "Assigned", icon: "👤" },
  { key: "picked_up", label: "Picked Up", icon: "🚗" },
  { key: "sorting", label: "Sorting", icon: "🔍" },
  { key: "processing", label: "Cleaning", icon: "🫧" },
  { key: "ready", label: "Ready", icon: "✅" },
  { key: "delivering", label: "Delivering", icon: "🚚" },
  { key: "delivered", label: "Delivered", icon: "🎉" },
];

export default function LaundryOrders() {
  const { lang } = useApp();
  const isAr = lang === "ar";
  const STATUS_TABS = isAr ? STATUS_TABS_AR : STATUS_TABS_EN;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [delegates, setDelegates] = useState([]);

  useEffect(() => {
    loadAll();
  }, []);

  const [laundryServices, setLaundryServices] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    const [ordersList, delegatesList, servicesList] = await Promise.all([
      base44.entities.LaundryOrder.list("-created_date", 500),
      base44.entities.Delegate.filter({ is_active: true }),
      base44.entities.LaundryService.filter({ is_active: true }),
    ]);
    setOrders(ordersList);
    setDelegates(delegatesList);
    setLaundryServices(servicesList);
    setLoading(false);
  };

  const filtered = activeTab === "all" ? orders : orders.filter(o => o.status === activeTab);
  const countByStatus = (key) => orders.filter(o => o.status === key).length;

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-foreground">🧺 {isAr ? "طلبات المغسلة" : "Laundry Orders"}</h1>
              <p className="text-muted-foreground text-sm">{orders.length} {isAr ? "طلب إجمالاً" : "total orders"}</p>
            </div>
            <button onClick={loadAll} className="px-4 py-2 bg-secondary rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors">
              🔄 {isAr ? "تحديث" : "Refresh"}
            </button>
          </div>

          {/* Status Tabs - scrollable */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
            {STATUS_TABS.map(tab => {
              const count = tab.key === "all" ? orders.length : countByStatus(tab.key);
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                    activeTab === tab.key
                      ? "bg-cyan-600 text-white shadow-md"
                      : "bg-card border border-border text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {tab.icon} {tab.label}
                  {count > 0 && (
                    <span className={`text-xs rounded-full w-5 h-5 flex items-center justify-center font-black ${
                      activeTab === tab.key ? "bg-white/20" : "bg-secondary"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="grid gap-3">
              {[1,2,3].map(i => <div key={i} className="h-28 bg-card rounded-2xl animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <div className="text-4xl mb-2">📭</div>
              <p>{isAr ? "لا توجد طلبات" : "No orders found"}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filtered.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onClick={() => setSelected(order)}
                />
              ))}
            </div>
          )}
        </div>

        {selected && (
          <OrderDetail
            order={selected}
            delegates={delegates}
            laundryServices={laundryServices}
            onClose={() => setSelected(null)}
            onUpdated={(updated) => {
              setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
              setSelected(updated);
            }}
          />
        )}
      </AdminLayout>
    </AdminGuard>
  );
}